import AbstractBaseApi from "@/apis/AbstractBaseApi";
import type {
  BandWidth,
  BandWidthEmpty,
  IfStatus,
  MaximumPrefix,
  Priority,
  RouteAggregation,
  VpnVnCode,
} from "@/apis/CloudVnMsApi";

export interface CloudVnKcps {
  /** E番号. */
  enumber: string;
  /**
   * アクセス品目.
   * 以下のいずれかを指定する。
   * MULTI_CLOUD_GW：マルチクラウドGW
   */
  accessType: "MULTI_CLOUD_GW";
  /** 帯域. GET時に登録された帯域が存在しない場合は「0」を指定する。 */
  bandwidth: BandWidth | BandWidthEmpty;
  /** 事業所名. */
  office: string | null;
  /**
   * クラウドVN回線(KCPS)排他情報. 排他確認用パラメータ
   * 設定変更を行う場合にはGETメソッドにて通知した内容をそのまま渡すこと。
   */
  cloudVnKcpsKeyId: string;
}

export interface CloudVnKcpsCommon {
  /** クラウドサービスキー情報.KCPSユーザーID */
  serviceKey: string;
  /**
   * クラウドサービス利用拠点.
   * 下記のいずれかを指定する。
   * TAMA: jp-kest-1(東日本)
   * TAKANAWA: jp-kest-2(東日本)
   * OSAKA: jp-kwst-1(西日本)
   */
  location: "TAMA" | "OSAKA" | "TAKANAWA";
  /** VPN/VNコード */
  vpnVnCode: VpnVnCode;
  /** 仮想ネットワークタイプ. L3固定 */
  vnType: "L3";
  /** 仮想ネットワーク名. */
  vnName: string | null;
  /** ルーティング種別. BGP4固定 */
  routingType: "BGP4";
  /** 帯域タイプ. PF固定 */
  bandwidthType: "PF";
  /** 帯域 */
  bandwidth: BandWidth;
  /** 非課金回線フラグ */
  billingFree: boolean;
  /** 非課金変更対象W番号(ACT) */
  assignFreeTo: string | null;
  /** 契約番号(M番号) */
  mNumber: string;
  /** 利用ゾーン */
  usageZone: string;
  /** AS番号. */
  asNumber: number;
  /** MD5文字列. */
  md5Key: string | null;
  /**
   * Maximum prefix(設定値上限取得APIのBGP4の最大経路数).
   * 設定値上限APIのGETから上限値を取得し、その上限値以下の値を設定可能とする。
   * 値は100,200,300,400,500,1000,1500,2000から指定する。
   */
  maximumPrefix: MaximumPrefix;
  /**
   * 経路集約.
   * OFF、ON、CUSTOMのいずれかを指定する。
   * OFF：経路集約を非表示
   * ON：経路集約を表示
   * CUSTOM：経路集約アドレスを入力
   */
  routeAggregation: RouteAggregation;
  /**
   * 経路集約アドレスリスト.
   * 経路集約が「CUSTOM」選択時は必須項目
   * 登録順でソートする。
   */
  routeAggregationAddressList: Array<string> | null;
  /** 設定値の説明  */
  description: string | null;
}

/** V2用共通情報（CloudVnKcpsCommonを拡張） */
export interface CloudVnKcpsCommonV2
  extends Omit<CloudVnKcpsCommon, "location"> {
  /**
   * クラウドサービス利用拠点(V2).
   * 下記のいずれかを指定する。
   * KEST1: jp-kest-1(東日本)
   * KWST1: jp-kwst-1(西日本)
   * KEST2: jp-kest-2(東日本)
   */
  location: "KEST1" | "KWST1" | "KEST2";
  /** プライベートLANID */
  privateLanId: string;
  /** 仮想IPアドレス */
  virtualIpAddress: string;
  /** Connectedルート広報 */
  publicRelation: boolean;
  /** BGP 広報ルート(ルート分割) */
  publicRelationSplitRouteList: string[] | null;
  /** BGP 広報ルート(Connected以外) */
  publicRelationConnectedExceptList: string[] | null;
  /** BGP 広報ルート(Connected以外のネクストホップ) */
  publicRelationNextHopList: string[] | null;
  /** バージョン情報 */
  version: "V1" | "V2";
  /**
   * プロビステータス
   */
  provStatus:
    | "SUCCESS"
    | "CREATING"
    | "CREATE_FAILED"
    | "RETRYABLE"
    | "FAILED"
    | "PENDING_DELETE"
    | "DELETE_RETRYABLE";
  /** エラー詳細 */
  errorDetail: string | null;
}

export interface CloudVnKcpsActSby {
  /** クラウドVNコネクトSEQ. */
  cloudVnConnectSeq: string;
  /** VNコネクト名. */
  vnConnectName: string;
  /**
   * W番号.
   * 主キー
   */
  wnumber: string;
  /**
   * IF状態. VNコネクト状態が無効の場合省略
   * NO_SHUTDOWN: 開放状態
   * SHUTDOWN: 閉塞状態
   */
  ifStatus: IfStatus;
  /** VNコネクト有効/無効フラグ. true:有効 false:無効 */
  isVnConnectStatus: boolean;
  /** WANアドレス. */
  wanAddress: string;
  /** ピアIPアドレス */
  peerIpAddress: string;
  /**
   * ローカルプリファレンス.
   * HIGH_PRIORITY:優先
   * LOW_PRIORITY:非優先
   * ACT回線識別フラグがtrueの場合:「HIGH_PRIORITY」固定
   * ACT回線識別フラグがfalseの場合:「LOW_PRIORITY」固定
   */
  localPreference: Priority;
  /**
   * MED.
   * HIGH_PRIORITY:優先
   * LOW_PRIORITY:非優先
   * ACT回線識別フラグがtrueの場合:「HIGH_PRIORITY」固定
   * ACT回線識別フラグがfalseの場合:「LOW_PRIORITY」固定
   */
  med: Priority;
  /**
   * VLAN_ID.
   */
  vlanId: number | null;
  /**
   * ネットワークID.
   */
  networkId: string | null;
}

/** V2用Act/Sby回線情報 */
export interface CloudVnKcpsActSbyV2
  extends Omit<CloudVnKcpsActSby, "vlanId" | "networkId"> {
  /** プライベートIPアドレス */
  privateIpAddress: string;
  /** VLAN ID(Inner) */
  vlanIdInner: number;
  /** VLAN ID(Outer) */
  vlanIdOuter: number;
  /** ステータス（クラウドコントローラから返却された値） */
  status: string;
}

/** 一覧取得レスポンス */
export type CloudVnKcpsList = CloudVnKcps & {
  cloudVnConnectTotal: number;
  cloudVnConnectsList: {
    cloudVnConnectCommon: Pick<
      CloudVnKcpsCommonV2,
      | "serviceKey"
      | "vpnVnCode"
      | "vnType"
      | "routingType"
      | "bandwidthType"
      | "bandwidth"
      | "billingFree"
      | "mNumber"
      | "description"
      | "location"
      | "version"
      | "provStatus"
    >;
    cloudVnConnectAct: Pick<
      CloudVnKcpsActSbyV2,
      | "cloudVnConnectSeq"
      | "vnConnectName"
      | "wnumber"
      | "ifStatus"
      | "isVnConnectStatus"
    >;
    cloudVnConnectSby:
      | CloudVnKcpsList["cloudVnConnectsList"][0]["cloudVnConnectAct"]
      | null;
  }[];
};

export type CloudVnKcpsDetail = CloudVnKcps & {
  cloudVnConnectCommon: CloudVnKcpsCommon;
  cloudVnConnectAct: CloudVnKcpsActSby;
  cloudVnConnectSby: CloudVnKcpsActSby | null;
};

/** V2詳細取得レスポンス */
export type CloudVnKcpsDetailV2 = CloudVnKcps & {
  cloudVnConnectCommon: CloudVnKcpsCommonV2;
  cloudVnConnectAct: CloudVnKcpsActSbyV2;
  cloudVnConnectSby: CloudVnKcpsActSbyV2;
};

/**
 * 設定追加には以下の3パターンがある。
 *
 * ACT/SBY回線を同時に追加するパターン
 * ACT回線のみを追加するパターン
 * 作成済みのACT回線に紐づくSBY回線を追加するパターン
 * また以下の項目は固定値となり、API内部で設定するため省略する。
 * ・MED
 *   ACT回線の場合は「HIGH_PRIORITY」固定
 *   SBY回線の場合は「LOW_PRIORITY」固定
 * ・ローカルプレファレンス
 *   ACT回線の場合は「HIGH_PRIORITY」固定
 *   SBY回線の場合は「LOW_PRIORITY」固定
 * ・ルーティング種別
 *   BGP4固定
 */
export interface CloudVnKcpsPost {
  /** ACT/SBY回線を同時に追加するパターン、ACT回線のみを追加するパターンでは必須となる。 */
  cloudVnConnectCommon: Pick<
    CloudVnKcpsCommon,
    | "serviceKey"
    | "location"
    | "mNumber"
    | "usageZone"
    | "vpnVnCode"
    | "bandwidthType"
    | "bandwidth"
    | "billingFree"
    | "routeAggregation"
    | "routeAggregationAddressList"
    | "asNumber"
    | "maximumPrefix"
    | "description"
  > | null;
  /** ACT/SBY回線を同時に追加するパターン、ACT回線のみを追加するパターンでは必須となる。 */
  cloudVnConnectAct: Pick<
    CloudVnKcpsActSby,
    "vnConnectName" | "wanAddress" | "peerIpAddress" | "networkId"
  > | null;
  /** ACT/SBY回線を同時に追加するパターン、SBY回線のみを追加するパターンでは必須となる。 */
  cloudVnConnectSby:
    | (CloudVnKcpsPost["cloudVnConnectAct"] & {
        /**
         * ペアクラウドVNコネクトSEQ.
         * SBY回線のみの設定の場合必須となる。
         * その場合ACT回線のクラウドVNコネクトSEQを指定する。
         */
        pairCloudVnConnectSeq: string | null;
      })
    | null;
  /** 操作履歴の備考 */
  note?: string;
  /**
   * クラウドVN回線(KCPS)排他情報. 排他確認用パラメータ
   * 設定変更を行う場合にはGETメソッドにて通知した内容をそのまま渡すこと。
   */
  cloudVnKcpsKeyId: string;
}

export interface CloudVnKcpsPut {
  cloudVnConnectCommon: Pick<
    CloudVnKcpsCommon,
    | "bandwidth"
    | "routeAggregation"
    | "routeAggregationAddressList"
    | "description"
    | "maximumPrefix"
    | "billingFree"
    | "assignFreeTo"
  >;
  cloudVnConnectAct: Pick<CloudVnKcpsActSby, "vnConnectName">;
  cloudVnConnectSby: Pick<CloudVnKcpsActSby, "vnConnectName"> | null;
  /** 操作履歴の備考 */
  note?: string;
  /**
   * クラウドVN回線(KCPS)排他情報. 排他確認用パラメータ
   * 設定変更を行う場合にはGETメソッドにて通知した内容をそのまま渡すこと。
   */
  cloudVnKcpsKeyId: string;
}

/** V2用追加リクエスト */
export interface CloudVnKcpsPostV2 {
  cloudVnConnectCommon: Pick<
    CloudVnKcpsCommonV2,
    | "serviceKey"
    | "location"
    | "mNumber"
    | "usageZone"
    | "vpnVnCode"
    | "bandwidthType"
    | "bandwidth"
    | "billingFree"
    | "routeAggregation"
    | "routeAggregationAddressList"
    | "asNumber"
    | "maximumPrefix"
    | "privateLanId"
    | "virtualIpAddress"
    | "publicRelation"
    | "publicRelationSplitRouteList"
    | "publicRelationConnectedExceptList"
    | "publicRelationNextHopList"
    | "description"
  >;
  cloudVnConnectAct: Pick<
    CloudVnKcpsActSbyV2,
    "vnConnectName" | "wanAddress" | "peerIpAddress" | "privateIpAddress"
  >;
  cloudVnConnectSby: Pick<
    CloudVnKcpsActSbyV2,
    "vnConnectName" | "wanAddress" | "peerIpAddress" | "privateIpAddress"
  >;
  note?: string;
  cloudVnKcpsKeyId: string;
}

/** V2用変更リクエスト */
export interface CloudVnKcpsPutV2 {
  cloudVnConnectCommon: Pick<
    CloudVnKcpsCommonV2,
    | "bandwidth"
    | "routeAggregation"
    | "routeAggregationAddressList"
    | "description"
    | "maximumPrefix"
    | "billingFree"
    | "assignFreeTo"
    | "publicRelation"
    | "publicRelationSplitRouteList"
    | "publicRelationConnectedExceptList"
    | "publicRelationNextHopList"
  >;
  cloudVnConnectAct: Pick<CloudVnKcpsActSbyV2, "vnConnectName">;
  cloudVnConnectSby: Pick<CloudVnKcpsActSbyV2, "vnConnectName">;
  note?: string;
  cloudVnKcpsKeyId: string;
}

export interface CloudVnKcpsOrder {
  /** W番号. 主キー */
  cloudVnConnectList: string[];
  /**
   * クラウドVN回線(KCPS)排他情報. 排他確認用パラメータ
   * 設定変更を行う場合にはGETメソッドにて通知した内容をそのまま渡すこと。
   */
  cloudVnKcpsKeyId: string;
}

export type CloudVnKcpsDelete = CloudVnKcpsOrder & {
  /** 操作履歴の備考 */
  note?: string;
};

/** V2用再実行リクエスト */
export interface CloudVnKcpsRetryV2 {
  /** クラウドVN回線(KCPS)排他情報 */
  cloudVnKcpsKeyId: string;
}

/** VNコネクト再起動リクエスト */
export interface CloudVnKcpsReboot {
  /** クラウドVN回線(KCPS)排他情報 */
  cloudVnKcpsKeyId: string;
}

/**
 * クラウドVN回線(KCPS3)API
 *
 * ## メソッド名の V1 / V2 と APIパスの v1 / v2 / v3 は別概念
 * - メソッド名末尾の「V2」は KCPS VNコネクト機能のバージョン（Ph2以降の新バージョン）を指す
 * - メソッド名に何も付かない（V1相当）は Ph1 の旧バージョンを指す
 * - APIパス中の「/v1/」「/v2/」「/v3/」はバックエンドAPIのリビジョンであり、
 *   KCPSエンティティのバージョン（V1/V2）とは無関係
 * - 各メソッドは常に最新のAPIパスを使用する。今後 /v4/ 等にアップデートされた場合は
 *   各メソッドのパスを更新すること
 */
export default class CloudVnKcpsApi extends AbstractBaseApi {
  // ==== 共通 API =====

  /**
   * クラウドVN回線設定一覧を取得
   * @param cloudLineSeq クラウド回線SEQ
   */
  async getVnConnects(cloudLineSeq: string): Promise<CloudVnKcpsList> {
    return (
      await this.api.get(
        `/v3/network/cloud/line/${cloudLineSeq}/kcps/vnconnects`
      )
    ).data;
  }

  /**
   * クラウドVNコネクトの有効/無効状態を有効に変更
   * V1・V2共通で使用する
   * @param data 有効情報
   */
  async putEnableVnConnect(data: CloudVnKcpsOrder): Promise<void> {
    await this.api.post(`/v1/network/cloud/line/kcps/vnconnects/enable`, data);
  }

  /**
   * クラウドVNコネクトの有効/無効状態を無効に変更
   * V1・V2共通で使用する
   * @param data 無効情報
   */
  async putDisableVnConnect(data: CloudVnKcpsOrder): Promise<void> {
    await this.api.post(`/v1/network/cloud/line/kcps/vnconnects/disable`, data);
  }

  // ===== V1（旧バージョン）向け API =====

  /**
   * クラウドVN回線設定を追加
   * @param cloudLineSeq クラウド回線SEQ
   * @param data クラウドVN回線設定
   */
  async postVnConnect(
    cloudLineSeq: string,
    data: CloudVnKcpsPost
  ): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/${cloudLineSeq}/kcps/vnconnects`,
      data
    );
  }

  /**
   * クラウドVN回線設定を変更
   * @param cloudLineSeq クラウド回線SEQ
   * @param wnumber W番号. ACT回線のW番号を指定する
   * @param data クラウドVN回線設定
   */
  async putVnConnect(
    cloudLineSeq: string,
    wnumber: string,
    data: CloudVnKcpsPut
  ): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/${cloudLineSeq}/kcps/vnconnects/${wnumber}`,
      data
    );
  }

  /**
   * クラウドVN回線設定の詳細情報を取得
   * @param wnumber W番号. ACT回線のW番号を指定する。
   */
  async getVnConnect(wnumber: string): Promise<CloudVnKcpsDetail> {
    return (
      await this.api.get(`/v1/network/cloud/line/kcps/vnconnect/${wnumber}`)
    ).data;
  }

  /**
   * クラウドVN回線設定を削除
   * @param cloudLineSeq
   * @param data
   */
  async deleteVnConnect(
    cloudLineSeq: string,
    data: CloudVnKcpsDelete
  ): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/${cloudLineSeq}/kcps/vnconnects/delete`,
      data
    );
  }

  // ===== V2（新バージョン・Ph2以降）向け API =====

  /**
   * クラウドVN回線設定を追加(V2)
   * @param cloudLineSeq クラウド回線SEQ
   * @param data クラウドVN回線設定
   */
  async postVnConnectV2(
    cloudLineSeq: string,
    data: CloudVnKcpsPostV2
  ): Promise<void> {
    await this.api.post(
      `/v2/network/cloud/line/${cloudLineSeq}/kcps/vnconnects`,
      data
    );
  }

  /**
   * クラウドVN回線設定を変更(V2)
   * @param cloudLineSeq クラウド回線SEQ
   * @param wnumber W番号. ACT回線のW番号を指定する
   * @param data クラウドVN回線設定
   */
  async putVnConnectV2(
    cloudLineSeq: string,
    wnumber: string,
    data: CloudVnKcpsPutV2
  ): Promise<void> {
    await this.api.post(
      `/v2/network/cloud/line/${cloudLineSeq}/kcps/vnconnects/${wnumber}`,
      data
    );
  }

  /**
   * クラウドVN回線設定の詳細情報を取得(V2)
   * @param wnumber W番号. ACT回線のW番号を指定する。
   */
  async getVnConnectV2(wnumber: string): Promise<CloudVnKcpsDetailV2> {
    return (
      await this.api.get(`/v3/network/cloud/line/kcps/vnconnect/${wnumber}`)
    ).data;
  }

  /**
   * クラウドVN回線設定を削除(V2)
   * @param cloudLineSeq
   * @param data
   */
  async deleteVnConnectV2(
    cloudLineSeq: string,
    data: CloudVnKcpsDelete
  ): Promise<void> {
    await this.api.post(
      `/v2/network/cloud/line/${cloudLineSeq}/kcps/vnconnects/delete`,
      data
    );
  }

  /**
   * クラウドVN回線設定を再実行(V2)
   * @param cloudLineSeq クラウド回線SEQ
   * @param wnumber W番号. ACT回線のW番号を指定する
   * @param data 再実行情報
   */
  async cloudVnKcpsRetryV2(
    cloudLineSeq: string,
    wnumber: string,
    data: CloudVnKcpsRetryV2
  ): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/${cloudLineSeq}/kcps/vnconnects/${wnumber}/retry`,
      data
    );
  }

  /**
   * VNコネクトを再起動(V2)
   * @param cloudLineSeq クラウド回線SEQ
   * @param wnumber W番号. ACT回線のW番号を指定する
   * @param data 再起動情報
   */
  async rebootVnConnect(
    cloudLineSeq: string,
    wnumber: string,
    data: CloudVnKcpsReboot
  ): Promise<void> {
    await this.api.post(
      `/v1/network/cloud/line/${cloudLineSeq}/kcps/vnconnects/${wnumber}/reboot`,
      data
    );
  }
}
